import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, Info, Sparkles, BadgePercent } from "lucide-react";
import { useLang } from "../contexts/LangContext";

// Starting price logic — transparent, conservative.
// All values in EUR (net). These are starting estimates; final pricing
// is confirmed individually after we review the request.
const PRICING = {
  lieferung: {
    base: 15,
    perKm: 1.5,
    sizes: { small: 0, medium: 8, large: 20 },
    note: "lieferung_note",
  },
  umzug: {
    base: 250,
    perKm: 2.5,
    sizes: { small: 0, medium: 250, large: 550 },
    note: "umzug_note",
    // Combo: includes Endreinigung (end-of-tenancy cleaning) at 20% off (150 → 120)
    combo: { id: "endreinigung", addPrice: 120, originalPrice: 150 },
  },
  reinigung: {
    base: 50,
    perKm: 0,
    sizes: { small: 0, medium: 50, large: 150 },
    hideDistance: true,
    note: "reinigung_note",
  },
  hausreinigung: {
    base: 69,
    perKm: 0,
    sizes: { small: 0, medium: 60, large: 140 },
    hideDistance: true,
    note: "hausreinigung_note",
    // 15% loyalty discount on the largest tier (recurring household plan)
    sizeDiscount: { large: 0.15 },
  },
  gartenpflege: {
    base: 79,
    perKm: 0,
    sizes: { small: 0, medium: 80, large: 220 },
    hideDistance: true,
    note: "gartenpflege_note",
    // 15% seasonal-contract discount on the largest tier
    sizeDiscount: { large: 0.15 },
  },
  autoteile: {
    base: 19,
    perKm: 1.20,
    sizes: { small: 0, medium: 89, large: 199 },
    note: "autoteile_note",
    // 20% workshop-contract discount on multi-run / daily plan
    sizeDiscount: { large: 0.20 },
  },
  regenrinne_reinigung: {
    base: 89,
    perKm: 1.2,
    sizes: { small: 0, medium: 60, large: 160 },
    note: "regenrinne_note",
    // 10% volume discount for Mehrfamilienhaus
    sizeDiscount: { large: 0.10 },
  },
  winterdienst: {
    base: 79,
    perKm: 0,
    sizes: { small: 0, medium: 70, large: 220 },
    hideDistance: true,
    note: "winterdienst_note",
    // 15% saisonvertrag bonus
    sizeDiscount: { large: 0.15 },
  },
  escooter_service: {
    contactOnly: true,
    note: "escooter_note",
  },
  plakatservice: {
    contactOnly: true,
    note: "plakatservice_note",
  },
};

const SIZE_KEYS = ["small", "medium", "large"];

function eur(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export default function PriceCalculator() {
  const { t } = useLang();
  const [service, setService] = useState("lieferung");
  const [size, setSize] = useState("medium");
  const [distance, setDistance] = useState(15);
  const [comboOn, setComboOn] = useState(false);

  const pricing = PRICING[service];

  const calc = useMemo(() => {
    if (!pricing || pricing.contactOnly) return null;
    const dist = pricing.hideDistance ? 0 : distance;
    const baseTotal = pricing.base + (pricing.sizes?.[size] || 0) + (pricing.perKm || 0) * dist;

    // Size-level percentage discount (e.g. Saisonvertrag, MFH-Bonus)
    const sizePct = pricing.sizeDiscount?.[size] || 0;
    const sizeDiscount = baseTotal * sizePct;

    // Combo add-on (only Umzug → +Endreinigung @ 20% off)
    let comboLine = 0;
    let comboSavings = 0;
    if (pricing.combo && comboOn) {
      comboLine = pricing.combo.addPrice;
      comboSavings = (pricing.combo.originalPrice || 0) - pricing.combo.addPrice;
    }

    const final = Math.round(baseTotal - sizeDiscount + comboLine);
    const originalShown = Math.round(baseTotal + (comboOn && pricing.combo ? pricing.combo.originalPrice : 0));
    const totalSaving = Math.round(sizeDiscount + comboSavings);

    return {
      final,
      originalShown,
      totalSaving,
      hasDiscount: totalSaving > 0,
      sizePct,
      comboActive: !!(pricing.combo && comboOn),
    };
  }, [pricing, size, distance, comboOn]);

  const c = t.calculator;

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="md:col-span-5">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{c.eyebrow}</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-6">{c.title}</h2>
            <p className="text-base text-neutral-600 leading-relaxed mb-6">{c.subtitle}</p>
            <div className="flex items-start gap-3 text-sm text-neutral-500 bg-[#F8F9FA] border border-neutral-200 p-4 rounded-sm">
              <Info size={18} className="text-[#FF5A00] shrink-0 mt-0.5" />
              <p>{c.disclaimer}</p>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="card-lift bg-[#0A0B0D] text-white p-8 lg:p-10 rounded-sm border border-neutral-800" data-testid="price-calculator">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-11 h-11 bg-[#FF5A00] flex items-center justify-center rounded-sm">
                  <Calculator size={22} />
                </div>
                <div>
                  <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00]">{c.tag}</div>
                  <div className="text-lg font-bold">{c.headline}</div>
                </div>
              </div>

              {/* Service select */}
              <label className="block mb-5">
                <div className="text-xs font-bold tracking-widest uppercase text-neutral-400 mb-2">{c.field_service}</div>
                <select
                  value={service}
                  onChange={(e) => { setService(e.target.value); setComboOn(false); }}
                  className="w-full bg-neutral-900 border border-neutral-700 text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-transparent rounded-sm"
                  data-testid="calc-service"
                >
                  {Object.keys(PRICING).map((k) => (
                    <option key={k} value={k}>{t.services[k]?.name || k}</option>
                  ))}
                </select>
              </label>

              {pricing.contactOnly ? (
                <div className="text-sm text-neutral-300 bg-neutral-900 border border-neutral-700 p-5 rounded-sm">
                  <p className="mb-4">{c[pricing.note]}</p>
                  <Link to="/book" className="btn-primary" data-testid="calc-contact-btn">
                    {c.cta_contact} <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
                <>
                  {/* Size */}
                  <div className="mb-5">
                    <div className="text-xs font-bold tracking-widest uppercase text-neutral-400 mb-2">{c.field_size}</div>
                    <div className="grid grid-cols-3 gap-2">
                      {SIZE_KEYS.map((s) => {
                        const sizeDeal = pricing.sizeDiscount?.[s];
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setSize(s)}
                            className={`relative px-3 py-2.5 text-sm font-bold rounded-sm border transition-all ${size === s ? "bg-[#FF5A00] border-[#FF5A00] text-white" : "bg-neutral-900 border-neutral-700 text-neutral-300 hover:border-neutral-500"}`}
                            data-testid={`calc-size-${s}`}
                          >
                            {c[`size_${s}_${service}`] || c[`size_${s}`]}
                            {sizeDeal ? (
                              <span className="absolute -top-2 -end-2 bg-[#10B981] text-white text-[9px] font-black px-1.5 py-0.5 rounded-sm tracking-wide shadow">
                                -{Math.round(sizeDeal * 100)}%
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Combo add-on (only for Umzug) */}
                  {pricing.combo && (
                    <button
                      type="button"
                      onClick={() => setComboOn(v => !v)}
                      className={`w-full mb-5 text-start flex items-start gap-3 p-4 rounded-sm border transition-all ${comboOn ? "bg-[#FF5A00]/10 border-[#FF5A00]" : "bg-neutral-900 border-neutral-700 hover:border-neutral-500"}`}
                      data-testid="calc-combo-toggle"
                    >
                      <div className={`mt-0.5 w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 ${comboOn ? "bg-[#FF5A00] border-[#FF5A00]" : "border-neutral-600"}`}>
                        {comboOn && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm flex items-center gap-1.5"><Sparkles size={14} className="text-[#FF5A00]" /> {c.combo_title}</span>
                          <span className="bg-[#10B981] text-white text-[10px] font-black px-2 py-0.5 rounded-sm tracking-wide">{c.combo_badge}</span>
                        </div>
                        <p className="text-xs text-neutral-400 mt-1 leading-relaxed">{c.combo_desc}</p>
                      </div>
                    </button>
                  )}

                  {/* Distance */}
                  {!pricing.hideDistance && (
                    <div className="mb-7">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold tracking-widest uppercase text-neutral-400">{c.field_distance}</div>
                        <div className="text-sm font-bold text-[#FF5A00]" data-testid="calc-distance-value">{distance} km</div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="150"
                        step="1"
                        value={distance}
                        onChange={(e) => setDistance(parseInt(e.target.value, 10))}
                        className="w-full accent-[#FF5A00]"
                        data-testid="calc-distance"
                      />
                      <div className="flex justify-between text-[10px] text-neutral-500 mt-1 font-medium">
                        <span>0 km</span><span>75 km</span><span>150+ km</span>
                      </div>
                    </div>
                  )}

                  {/* Result */}
                  <div className="bg-gradient-to-br from-[#FF5A00] to-[#E04F00] p-6 rounded-sm relative overflow-hidden" data-testid="calc-result">
                    {calc.hasDiscount && (
                      <span className="absolute top-3 end-3 bg-[#10B981] text-white text-[11px] font-black px-2.5 py-1 rounded-sm tracking-wide shadow flex items-center gap-1" data-testid="calc-savings-badge">
                        <BadgePercent size={12} /> {c.you_save} {eur(calc.totalSaving)}
                      </span>
                    )}
                    <div className="text-xs font-bold tracking-[0.25em] uppercase text-white/80 mb-1">{c.result_label}</div>
                    <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                      <span className="text-sm font-bold text-white/80">{c.from}</span>
                      <span className="text-5xl sm:text-6xl font-black tracking-tighter text-white">{eur(calc.final)}</span>
                      {calc.hasDiscount && (
                        <span className="text-lg font-bold text-white/60 line-through" data-testid="calc-original-price">{eur(calc.originalShown)}</span>
                      )}
                    </div>
                    <p className="text-xs text-white/80 mt-3 mb-5">{c[pricing.note]}</p>
                    <Link to="/book" className="inline-flex items-center gap-2 bg-white text-[#FF5A00] font-bold tracking-wide px-6 py-3 hover:bg-neutral-100 transition-colors rounded-sm" data-testid="calc-book-btn">
                      {c.cta_book} <ArrowRight size={16} />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
